import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { IPartCancelRequest, IPartImagePatchRequest, IPartMarketplaces, IPartQcRequest, ISingleCategoryPartResponse, PartImageRequirementType } from "../models/Part";
import { IInvoice } from "../models/invoice/IInvoice";
import { DefaultResponseWithData } from "../models/system";

export default function useSinglePart(partId: string | undefined) {
  const { get, post, put, patch, postDocs, del } = useApiHelper();
  const queryClient = useQueryClient();
  const staleTime = 1000 * 60; //1 minutes

  const singlePart = useQuery({
    queryKey: ["parts", partId],
    queryFn: () => get<ISingleCategoryPartResponse>(`/part/${partId}/offers`),
    staleTime: staleTime,
    refetchOnWindowFocus: false,
    // refetchInterval: staleTime, //No refetch interval to stop the editor from refreshing
  });

  const invoice = useQuery({
    queryKey: ["parts", partId, "invoice"],
    queryFn: () => get<DefaultResponseWithData<IInvoice>>(`/part/${partId}/invoice`),
    staleTime: staleTime,
    refetchOnWindowFocus: false,
  });


  const patchPart = useMutation(
    (req: IPartQcRequest) => patch(`/part/${req.partId}`, req),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["parts", partId]);
      },
    }
  );

  const publish = useMutation(
    (offerId: string) => post(`/part/offers/${offerId}/publish`, null),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["parts", partId]);
      },
    }
  );

  const createOffer = useMutation(
    (partId: string) => post(`/part/offers/default/${partId}`, null),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["parts", partId]);
        queryClient.invalidateQueries(["parts", "multiple"]);
      },
    }
  );

  const withdraw = useMutation(
    (offerId: string) => post(`/part/offers/${offerId}/withdraw`, null),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["parts", partId]);
      },
    }
  );

  const cancel = useMutation(
    (req: IPartCancelRequest) => post(`/part/cancelorder`, req),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["parts", partId]);
      },
    }
  );

  const uploadPicture = useMutation(
    ({ data, imageType }: { data: FormData, imageType?: PartImageRequirementType }) => postDocs(`/part/${partId}/upload${imageType ? `?imageType=${imageType}` : ''}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["parts", partId]);
        queryClient.invalidateQueries(["pagedTable"]);
      },
    }
  );

  const autoReorderImages = useMutation(
    (ids: any) => post(`/part/images/autoOrder`, ids),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(["job"]);
        // Invalidate and refetch for each part ID in the array
        if (Array.isArray(variables)) {
          variables.forEach((partId: string) => {
            queryClient.invalidateQueries(["parts", partId]);
            queryClient.refetchQueries(["parts", partId]);
          });
        }
        // Also invalidate all parts queries as fallback
        queryClient.invalidateQueries(["parts"]);
        queryClient.invalidateQueries(["pagedTable"]);
      },
    }
  );

  const removeImage = useMutation(
    (imageId: string) => del(`/part/images/${imageId}`,),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["parts", partId]);
        queryClient.invalidateQueries(["pagedTable"]);
      },
    }
  );

  const removePictureBg = useMutation(
    (imageId: string | undefined) => get(`/part/images/${imageId}/removebg`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["parts", partId]);
        queryClient.invalidateQueries(["pagedTable"]);
      },
    }
  );

  const patchPicture = useMutation(
    (data: IPartImagePatchRequest[]) => patch(`/part/images/bulk`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["parts", partId]);
        queryClient.refetchQueries(["parts", partId]);
        queryClient.invalidateQueries(["pagedTable"]);
      },
    }
  );

  const possibleMarketplaces = useQuery({
    queryKey: ["marketplaces", "possible"],
    queryFn: () => get<DefaultResponseWithData<IPartMarketplaces>>(`/part/marketplaces`),
    staleTime: staleTime,
    refetchOnWindowFocus: false,
  });

  const marketplaces = useQuery({
    queryKey: ["marketplaces", partId],
    queryFn: () => get<DefaultResponseWithData<IPartMarketplaces>>(`/part/${partId}/marketplaces`),
    staleTime: staleTime,
    refetchOnWindowFocus: false,
  });

  const updateMarketplaces = useMutation(
    (marketplaces: IPartMarketplaces) => put(`/part/${partId}/marketplaces`, marketplaces),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["marketplaces", partId]);
      },
    }
  ); 


  return {
    patchPart,
    singlePart,
    publish,
    createOffer,
    cancel,
    withdraw,
    uploadPicture,
    autoReorderImages,
    removeImage,
    patchPicture,
    removePictureBg,
    invoice,
    possibleMarketplaces,
    marketplaces,
    updateMarketplaces,

  };
}
